import {
  getUserAttribute,
  getUserAttributes,
  getTranslatedUserAttributes,
  deleteUserAttribute,
  getReportsUserAttributes,
  saveUserAttribute,
  updateUserAttribute,
  requestUserAttributesReport,
} from '@/service/user-attributes'

const value = () => ({
  label: {},
})

const initialState = () => ({
  requestLoading: false,
  detail: {
    loading: false,
    userAttribute: {
      label: {},
      values: [value(), value()], // default values
    },
  },
  list: {
    loading: false,
    userAttributes: [],
    total: 0,
  },
  filter: {
    loading: false,
    userAttributes: [],
    total: 0,
  },
  reports: {
    selected: undefined,
    loading: false,
    items: [],
  },
})

const state = initialState()

const getters = {
  requestLoading: state => state.requestLoading,
  detailLoading: state => state.detail.loading,
  detailUserAttribute: state => state.detail.userAttribute,
  listLoading: state => state.list.loading,
  listUserAttributes: state => state.list.userAttributes,
  listTotal: state => state.list.total,
  filterLoading: state => state.filter.loading,
  filterUserAttributes: state => state.filter.userAttributes,
  filterTotal: state => state.filter.total,
  reportsLoading: state => state.reports.loading,
  reportsUserAttributes: state => state.reports.items,
  selectedReport: state => state.reports.selected,
}

const mutations = {
  moveUpValue(state, { index }) {
    const from = index
    const to = index - 1
    state['detail']['userAttribute'].values.splice(
      to,
      0,
      state['detail']['userAttribute'].values.splice(from, 1)[0]
    )
  },
  moveDownValue(state, { index }) {
    const from = index
    const to = index + 1
    state['detail']['userAttribute'].values.splice(
      to,
      0,
      state['detail']['userAttribute'].values.splice(from, 1)[0]
    )
  },
  setRequestLoading(state, loading) {
    state.requestLoading = loading
  },
  clearUserAttribute(state) {
    state.detail.userAttribute = initialState().detail.userAttribute
  },
  setDetailLoading(state, loading) {
    state.detail.loading = loading
  },
  setDetailUserAttribute(state, userAttribute) {
    state.detail.userAttribute = userAttribute
  },
  setListLoading(state, loading) {
    state.list.loading = loading
  },
  setListUserAttributes(state, userAttributes) {
    state.list.userAttributes = userAttributes
  },
  setListTotal(state, total) {
    state.list.total = total
  },
  setFilterLoading(state, loading) {
    state.filter.loading = loading
  },
  setFilterUserAttributes(state, userAttributes) {
    state.filter.userAttributes = userAttributes
  },
  setFilterTotal(state, total) {
    state.filter.total = total
  },
  setLanguageLabelProperty(state, { value, language }) {
    if (!value && value === '') {
      delete state.detail.userAttribute.label[language]
    } else {
      state.detail.userAttribute.label[language] = value
    }
  },
  setLanguageValueLabelProperty(state, { index, value, language }) {
    if (!value && value === '') {
      delete state.detail.userAttribute.values[index].label[language]
    } else {
      state.detail.userAttribute.values[index].label[language] = value
    }
  },
  addValue(state) {
    state.detail.userAttribute.values.push(value())
  },
  setReportsLoading(state, loading) {
    state.reports.loading = loading
  },
  setReportsUserAttributes(state, reports) {
    state.reports.items = reports
  },
  removeValue(state, index) {
    state.detail.userAttribute.values = state.detail.userAttribute.values.filter(
      (_, i) => i !== index
    )
  },
  selectReport(state, report) {
    state.reports.selected = report
  },
}

const actions = {
  moveUpValue({ commit }, { index }) {
    commit('moveUpValue', { index })
  },
  moveDownValue({ commit }, { index }) {
    commit('moveDownValue', { index })
  },
  setRequestLoading({ commit }, loading) {
    commit('setRequestLoading', loading)
  },
  clearUserAttribute({ commit }) {
    commit('clearUserAttribute')
  },
  loadUserAttribute({ commit }, id) {
    commit('setDetailLoading', true)
    return getUserAttribute(id).then(({ data }) => {
      const { userAttribute } = data
      commit('setDetailUserAttribute', userAttribute)
      commit('setDetailLoading', false)
    })
  },
  removeUserAttribute(_, { id }) {
    return deleteUserAttribute(id)
  },
  loadUserAttributesList({ commit }, { offset, limit } = {}) {
    commit('setListLoading', true)
    return getUserAttributes({ offset, limit }).then(({ data }) => {
      const { userAttributes, metadata } = data
      commit('setListUserAttributes', userAttributes)
      commit('setListTotal', metadata.total)
      commit('setListLoading', false)
    })
  },
  loadUserAttributesFilter({ commit }, { offset, limit, hidden } = {}) {
    commit('setFilterLoading', true)
    return getTranslatedUserAttributes({ offset, limit, hidden }).then(
      ({ data }) => {
        const { userAttributes, metadata } = data
        commit('setFilterUserAttributes', userAttributes)
        commit('setFilterTotal', metadata.total)
        commit('setFilterLoading', false)
      }
    )
  },
  loadUserAttributesReports({ commit }, { groupId, unselect }) {
    if (unselect) {
      commit('selectReport', undefined)
    }
    commit('setReportsLoading', true)
    return getReportsUserAttributes(groupId).then(({ data }) => {
      commit('setReportsUserAttributes', data)
      commit('setReportsLoading', false)
    })
  },
  changeLanguageLabelProperty({ commit }, { value, language }) {
    commit('setLanguageLabelProperty', { value, language })
  },
  changeLanguageValueLabelProperty({ commit }, { index, value, language }) {
    commit('setLanguageValueLabelProperty', { index, value, language })
  },
  addValue({ commit }) {
    commit('addValue')
  },
  removeValue({ commit }, index) {
    commit('removeValue', index)
  },
  save(_, userAttribute) {
    return saveUserAttribute(userAttribute)
  },
  update(_, userAttribute) {
    return updateUserAttribute(userAttribute)
  },
  selectReport({ commit }, report) {
    commit('selectReport', report)
  },
  requestUserAttributesReport(_, { id, userAttributes }) {
    return requestUserAttributesReport(id, userAttributes)
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
